import moment from "moment-timezone";
import { FacilityPolicyScope } from "types/enums/facility-policy/facility-policy-enums";
import { IEffectiveFacilityPolicy } from "types/interfaces/facility-policy/effective-facility-policy";

/**
 * Filter policies that overlap the given time range.
 *
 * Overlap check:
 * - Starts before, ends during the window
 * - Starts during, ends after the window
 * - Fully contains the window
 * - Fully inside the window
 */
export const filterPoliciesOverlappingPeriod = (
  policies: IEffectiveFacilityPolicy[],
  periodStart: moment.MomentInput,
  periodEnd: moment.MomentInput,
): IEffectiveFacilityPolicy[] => {
  const start = moment(periodStart);
  const end = moment(periodEnd);

  return policies.filter((policy) => {
    const policyStart = moment(policy.startTime);
    const policyEnd = policy.endTime ? moment(policy.endTime) : null;

    // Overlap check: policy starts before period ends AND policy ends after period starts
    const startsBeforePeriodEnds = policyStart.isBefore(end);
    const endsAfterPeriodStarts = policyEnd ? policyEnd.isAfter(start) : true;

    return startsBeforePeriodEnds && endsAfterPeriodStarts;
  });
};

/**
 * Filter policies that overlap the given time range and match the given scope.
 * Handles perpetual closures (policies without an endTime).
 */
export const timeAndScopeFilterPolicy = (
  policies: IEffectiveFacilityPolicy[],
  start: string,
  end: string,
  scope: FacilityPolicyScope,
) => {
  const overlapping = filterPoliciesOverlappingPeriod(policies, start, end);
  return overlapping.filter((policy) => policy.effects.some((e) => e.scope === scope));
};

export const officeFilterPolicy = (officeUuid: string, policies: IEffectiveFacilityPolicy[]) => {
  return policies.filter(
    (policy) => policy.officeUuid === officeUuid && !policy.floorUuid && !policy.zoneUuid && !policy.resourceUuid,
  );
};

/** Filter policies that have an officeUuid but no floorUuid, zoneUuid or resourceUuid */
export const officeFilterPolicyAll = (policies: IEffectiveFacilityPolicy[]) => {
  return policies.filter(
    (policy) => policy.officeUuid && !policy.floorUuid && !policy.zoneUuid && !policy.resourceUuid,
  );
};

export const floorFilterPolicy = (floorUuid: string, policies: IEffectiveFacilityPolicy[]) => {
  return policies.filter((policy) => policy.floorUuid === floorUuid && !policy.zoneUuid && !policy.resourceUuid);
};

/** Filter policies that have a floorUuid matching the given floorUuid */
export const floorFilterPolicyAllDepths = (floorUuid: string, policies: IEffectiveFacilityPolicy[]) => {
  return policies.filter((policy) => policy.floorUuid === floorUuid);
};

/** Filter policies that have a floorUuid but no zoneUuid or resourceUuid */
export const floorFilterPolicyAll = (policies: IEffectiveFacilityPolicy[]) => {
  return policies.filter((policy) => policy.floorUuid && !policy.zoneUuid && !policy.resourceUuid);
};

export const zoneFilterPolicy = (zoneUuid: string, policies: IEffectiveFacilityPolicy[]) => {
  return policies.filter((policy) => (!zoneUuid || policy.zoneUuid === zoneUuid) && !policy.resourceUuid);
};

/** Filter policies that have a zoneUuid but no resourceUuid */
export const zoneFilterPolicyAll = (policies: IEffectiveFacilityPolicy[]) => {
  return policies.filter((policy) => policy.zoneUuid && !policy.resourceUuid);
};

export const resourceFilterPolicy = (resourceUuid: string, policies: IEffectiveFacilityPolicy[]) => {
  return policies.filter((policy) => policy.resourceUuid === resourceUuid);
};

/** Filter policies that have a resourceUuid */
export const resourceFilterPolicyAll = (policies: IEffectiveFacilityPolicy[]) => {
  return policies.filter((policy) => policy.resourceUuid);
};
